import { http } from '@/api/http';
import { OperationStateTypes, OperationStatusResponse, OrchestratorResourcesBody } from './orchestrator.types';
import { getOrchestratorApiUrl } from './orchestrator.paths';
import { ApiResponseBody, Uuid } from '@/api/types';
import type { Case } from '@/api/apis/case';

const reserveResources = async (caseId: string, body: Partial<OrchestratorResourcesBody>) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.reserveResource(caseId),
    body,
  );
  return res.data.data;
};

const removeReservedResources = async (caseId: string, body: Partial<OrchestratorResourcesBody>) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.removeResourceReservation(caseId),
    body,
  );
  return res.data.data;
};

const bookResources = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(getOrchestratorApiUrl.bookResources(caseId));
  return res.data.data;
};

const bookSpecificResources = async (caseId: string, body: Partial<OrchestratorResourcesBody>) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.bookSpecificResources(caseId),
    body,
  );
  return res.data.data;
};

const switchConsole = async (caseId: string, previousOcsConsoleId: string, ocsConsoleId: string) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(getOrchestratorApiUrl.switchConsole(caseId), {
    previousOcsConsoleId,
    ocsConsoleId,
  });
  return res.data.data;
};

const transferResources = async (fromCaseId: Uuid, toCaseId: Uuid) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(getOrchestratorApiUrl.transferResources(), {
    fromCaseId,
    toCaseId,
  });
  return res.data.data;
};

const transferReallocationData = async (fromCaseId: Uuid, toCaseId: Uuid) => {
  const res = await http.post<ApiResponseBody<Case>>(getOrchestratorApiUrl.transferReallocation(), {
    fromCaseId,
    toCaseId,
  });
  return res.data.data;
};

const switchClinicalSpecialist = async (caseId: string, previousResourceId: string, resourceId: string) => {
  const res = await http.post<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.switchClinicalSpecialist(caseId),
    {
      previousResourceId,
      resourceId,
    },
  );
  return res.data.data;
};

const checkOperationStatus = async (correlationId: string, stateType: OperationStateTypes) => {
  const res = await http.get<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.checkOperationStatus(correlationId),
    { params: { sagaStateType: stateType } },
  );
  return res.data.data;
};

const removeHospitalContact = async (contactId: string) => {
  const res = await http.delete<{
    data: { correlationId?: string; message: string | null };
  }>(getOrchestratorApiUrl.removeHospitalContactById(contactId));
  return res.data.data;
};

const removeHospitalContactWithCorrelation = async (contactId: string) => {
  const res = await http.delete<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.removeHospitalContactById(contactId),
  );
  return res.data.data;
};

const removeOpoContact = async (contactId: string) => {
  await http.delete<void>(getOrchestratorApiUrl.removeOpoContactById(contactId));
};

const removeOpoById = async (opoId: string) => {
  await http.delete<void>(getOrchestratorApiUrl.removeOpoById(opoId));
};

const removeCustomerById = async (customerId: string) => {
  const res = await http.delete<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.removeCustomerById(customerId),
  );
  return res.data.data;
};

const removeHospitalById = async (hospitalId: string) => {
  const res = await http.delete<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.removeHospitalById(hospitalId),
  );
  return res.data.data;
};

const removeTXCenterById = async (txCenterId: string) => {
  const res = await http.delete<ApiResponseBody<OperationStatusResponse>>(
    getOrchestratorApiUrl.removeTXCenterById(txCenterId),
  );
  return res.data.data;
};

const removeHubById = async (hubId: string) => {
  const res = await http.delete<ApiResponseBody<OperationStatusResponse>>(getOrchestratorApiUrl.hubById(hubId));
  return res.data.data;
};

export const orchestratorApiFetcher = {
  reserveResources,
  removeReservedResources,
  bookResources,
  bookSpecificResources,
  checkOperationStatus,
  switchConsole,
  transferResources,
  transferReallocationData,
  switchClinicalSpecialist,
  removeHospitalContact,
  removeHospitalContactWithCorrelation,
  removeOpoContact,
  removeOpoById,
  removeCustomerById,
  removeHospitalById,
  removeTXCenterById,
  removeHubById,
};
